import { BsDatepickerConfig } from "ngx-bootstrap/datepicker";
import { environment } from "dm-src/environments/environment";
import { DeliveryPlanType } from "dm-src/types/delivery-plan-type";

/** Central configuration of the delivery plan viewer dashboard */

export const DATE_FORMAT = "YYYY-MM-DD";
export const TIME_FORMAT = "HH:mm";

export const DELIVERY_PLAN_TYPE = DeliveryPlanType.Published;

export const STOP_TYPE_PICKUP = "pickup";
export const STOP_TYPE_DELIVERY = "delivery";

export const COURIER_ROUTE_COLORS = ["#2196F3", "#F44336", "#4CAF50", "#FF9800", "#9C27b0", "#009688"];

/** Keep in sync: --dpv-color-pickup / --dpv-color-delivery in _delivery-plan-viewer.styles.scss */
export const PICKUP_COLOR = "#FF5722";
export const DELIVERY_COLOR = "#3F51B5";
export const UNASSIGNED_COLOR = "#9E9E9E";

/** Hex alpha appended to a courier color for the selected legend row"s background (26 = 15%) */
export const LEGEND_SELECTED_BACKGROUND_ALPHA = "26";

/** Tile URL template of the base map, per environment (environment.mapTileUrl) */
export const MAP_TILE_URL: string = environment.mapTileUrl;

/** Required by the OSM license and the tile provider */
export const MAP_TILE_ATTRIBUTION: string = environment.mapTileAttribution;

/**
 * Text before the attribution; false = none. Leaflet"s default prefix (its flag icon and a
 * "Leaflet" link) is removed: it is not a license requirement (Leaflet is BSD-2, which only asks
 * to keep the license notice in the code).
 */
export const MAP_ATTRIBUTION_PREFIX: string | false = false;

export const MAP_MAX_ZOOM = 19;

// Higher = fewer empty areas while panning, more memory.
export const MAP_TILE_KEEP_BUFFER = 6;

/** How often new tiles are loaded while dragging, in ms (Leaflet default 200). */
export const MAP_TILE_UPDATE_INTERVAL_MS = 100;

/**
 * Extra area drawn around the view for the route lines, in view sizes per side (Leaflet default
 * 0.1). Lines outside this area are only drawn when a drag ends, so 1 keeps them visible while
 * dragging up to a full screen.
 */
export const MAP_VECTOR_RENDERER_PADDING = 1;

/** Map center before the plan is loaded (Budapest). */
export const MAP_INITIAL_CENTER: [number, number] = [47.4979, 19.0402];

export const MAP_INITIAL_ZOOM = 11;

/**
 * After loading, the map fits all visible stops. It never zooms in further than this, so a few
 * stops close to each other don"t end up at street level.
 */
export const MAP_FIT_MAX_ZOOM = 15;

/** Free space kept around the stops when fitting, in px. */
export const MAP_FIT_PADDING_PX = 40;

/**
 * Zoom levels to step back after the fit, so the stops don"t fill the whole view. Each level
 * shows twice the area per direction; 0 = tight fit.
 */
export const MAP_FIT_ZOOM_OUT_LEVELS = 1;

/** Width of the route lines, in px. */
export const ROUTE_LINE_WEIGHT = 4;

/** z-index offset that puts the selected marker on top of the others. */
export const SELECTED_MARKER_Z_INDEX_OFFSET = 1000;

/** Size of a marker"s box (badge + order code + ETA), in px. */
export const MARKER_ICON_SIZE: [number, number] = [120, 60];

/** Point of the marker box placed on the stop's location: the middle of the badge, in px. */
export const MARKER_ICON_ANCHOR: [number, number] = [60, 12];

/** Decimals of the coordinates in the order details dialog. */
export const COORDINATE_DECIMALS = 5;

export const MOBILE_MEDIA_QUERY = "(max-width: 699.98px)";

/** Keep in sync: --dpv-stops-panel-width. */
export const STOPS_PANEL_WIDTH_PX = 240;

export const SHEET_COLLAPSED_SIZE = 0.14;
export const SHEET_EXPANDED_SIZE = 0.92;
export const SHEET_ANIMATION = "height 220ms cubic-bezier(0.215, 0.61, 0.355, 1)";
export const SHEET_TAP_THRESHOLD_PX = 5;

export const COURIER_ID_LABEL_LENGTH = 8;

export const TOAST_ERROR_DURATION_MS = 4000;
export const TOAST_COPY_DURATION_MS = 2000;

export const DATE_PICKER_MIN_DATE = new Date(2020, 0, 1);
export const DATE_PICKER_MAX_DATE = new Date(2030, 11, 31);

export const DATE_PICKER_CONFIG: Partial<BsDatepickerConfig> =
{
    containerClass: "theme-dark-blue",
    showWeekNumbers: false,
    adaptivePosition: true
};

/**
 * CSS selector of the element the date picker popup is attached to: the left panel itself.
 * ngx-bootstrap"s default ("body") renders the popup outside the fullscreen modal, beneath it.
 */
export const DATE_PICKER_CONTAINER = "app-delivery-plan-viewer-left-panel";

/** Resolution multiplier of the exported image (2 = 640px wide for the 320px export copy). */
export const EXPORT_PIXEL_RATIO = 2;
export const EXPORT_BACKGROUND_COLOR = "#FFFFFF";
export const EXPORT_FILE_PREFIX = "route_";

/**
 * Classes used in styles.scss: transparent backdrop (no double dimming) and a dialog shadow.
 * The window class is also used in delivery-plan-viewer.component.scss (no full screen on mobile).
 */
export const DETAILS_DIALOG_BACKDROP_CLASS = "delivery-plan-viewer-details-backdrop";
export const DETAILS_DIALOG_WINDOW_CLASS = "delivery-plan-viewer-details-window";